import React, { useEffect } from 'react';
import { useAppDispatch } from 'src/hook/useAppDispatch';
import { useAppSelector } from 'src/hook/useAppSelector';

import { 
    setAuthenticated, 
    setUser, 
    setLoading, 
    setError,
    setRoles,
    selectIsLoading,
    selectIsAuthenticated
} from 'src/store/slice/authSlice';
import type { UserRole } from './auth.types';

import { ensureAuthenticated } from './auth';
import { Spinner } from '@amzn/awsui-components-react';
import { Hub } from '@aws-amplify/core';
import { signOut, fetchAuthSession } from '@aws-amplify/auth';
import { useTokenRefresh } from './useTokenRefresh';
import { tokenCache } from './tokenCache';
import { CustomAuthEvents } from './auth.types';
import { logger } from 'src/logger';


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectIsLoading);
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const { scheduleTokenRefresh } = useTokenRefresh();

    useEffect(() => {
        const extractAndDispatchRoles = (session: Awaited<ReturnType<typeof fetchAuthSession>>) => {
            const payload = session.tokens?.idToken?.payload as Record<string, unknown> | undefined;
            // AmazonFederate injects the claim as a JSON string, array, or space-separated string
            const raw = payload?.['custom:roles'] ?? payload?.['roles'];
            let parsed: unknown[];
            try {
                const jsonResult = Array.isArray(raw) ? raw : JSON.parse(String(raw ?? '[]'));
                parsed = Array.isArray(jsonResult) ? jsonResult : [jsonResult];
            } catch {
                parsed = String(raw ?? '').split(' ');
            }
            const roles: UserRole[] = parsed
                .map((r) => String(r).trim())
                .filter((r): r is UserRole => r === 'admin' || r === 'uat');
            dispatch(setRoles(roles));
        };

        const initAuth = async () => {
            try {
                const user = await ensureAuthenticated();
                dispatch(setUser(user));
                dispatch(setAuthenticated(true));

                const session = await fetchAuthSession();
                extractAndDispatchRoles(session);
                const expiration = session.tokens?.idToken?.payload.exp;
                if (expiration) {
                    scheduleTokenRefresh(expiration * 1000);
                }
            } catch (error) {
                logger.error('Authentication failed:', error as Error);
                dispatch(setAuthenticated(false));
                dispatch(setError(error instanceof Error ? error.message : 'Authentication failed'));
            } finally {
                dispatch(setLoading(false));
            }
        };

        // Listen for auth events
        const unsubscribe = Hub.listen('auth', async ({ payload }: { payload: CustomAuthEvents }) => {
            switch (payload.event) {
                case 'signedIn':
                    const user = await ensureAuthenticated();
                    dispatch(setUser(user));
                    dispatch(setAuthenticated(true));
                    
                    // Schedule token refresh after sign in
                    const session = await fetchAuthSession();
                    extractAndDispatchRoles(session);
                    const expiration = session.tokens?.idToken?.payload.exp;
                    if (expiration) {
                        scheduleTokenRefresh(expiration * 1000);
                    }
                    break;
                case 'signedOut':
                    dispatch(setUser(null));
                    dispatch(setAuthenticated(false));
                    dispatch(setRoles([]));
                    break;
                case 'tokenRefresh_success':
                    try {
                        const refreshedSession = await fetchAuthSession();
                        extractAndDispatchRoles(refreshedSession);
                    } catch (e) {
                        logger.error('Failed to refresh roles:', e as Error);
                    }
                    scheduleTokenRefresh(payload.data.expirationMs);
                    break;
                case 'tokenRefresh_failure':
                    logger.error('Token refresh failed:', payload.data as Error);
                    if (isAuthenticated) {
                        try {
                            // Clear token cache before signing out
                            tokenCache.clearAll();
                            await signOut({ global: true });
                            window.location.reload(); // Force reload to clear any stale state
                        } catch (error) {
                            logger.error('Sign out failed:', error as Error);
                        }
                    }
                    break;
            }
        });

        initAuth();

        return () => {
            unsubscribe();
        };
    }, [dispatch]);

    if (isLoading) {
        return   <Spinner size="big" />;
    }

    return <>{children}</>;
};

export default AuthProvider;